import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera, MessageSquarePlus, Send, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { installConsoleCapture, getConsoleTail } from '../../utils/consoleCapture';
import { submitFeedback } from '../../services/feedbackService';
import { useRegisterTopEscapeClose } from '../../hooks/useEscapeCloseStack';
import {
  FLOATING_DOCK_EVENT,
  DOCK_PANEL_WIDTH,
  DOCK_PANEL_HEIGHT,
  DOCK_PANEL_RIGHT,
  DOCK_PANEL_BOTTOM,
  DOCK_RIGHT,
  DOCK_BOTTOM,
  FLOATING_DOCK_CLASS,
  announceDockOpen,
} from '../../utils/floatingDocks';

const CATEGORY_OPTIONS = [
  { id: 'improvement', labelKey: 'feedback.category.improvement' },
  { id: 'bug', labelKey: 'feedback.category.bug' },
  { id: 'feature', labelKey: 'feedback.category.feature' },
];

async function captureViewportPngBlob() {
  const canvas = await html2canvas(document.body, {
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    scrollX: window.scrollX,
    scrollY: window.scrollY,
    scale: 1,
  });
  return await new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
}

/**
 * Floating feedback dock (FAB + panel).
 *
 * @param {{
 *  open: boolean,
 *  onOpenChange: (v: boolean) => void,
 *  currentStep?: string,
 *  currentProperty?: { id?: string } | null,
 * }} props
 */
export default function FloatingFeedbackDock({
  open,
  onOpenChange,
  currentStep,
  currentProperty,
}) {
  const { currentUser } = useAuth();
  const { t } = useLanguage();

  const [category, setCategory] = useState('improvement');
  const [message, setMessage] = useState('');
  const [includeConsole, setIncludeConsole] = useState(true);
  const [screenshotBlob, setScreenshotBlob] = useState(null);
  const [screenshotPreviewUrl, setScreenshotPreviewUrl] = useState('');
  const [screenshotBusy, setScreenshotBusy] = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [sent, setSent] = useState(false);
  const [captureOk, setCaptureOk] = useState(false);

  const messageRef = useRef(null);

  useRegisterTopEscapeClose(() => onOpenChange(false), open);

  // Clear success state as soon as user starts a new feedback.
  useEffect(() => {
    if (!sent) return;
    // Any interaction that suggests a new report.
    if (message.trim().length > 0 || screenshotBlob || category) {
      setSent(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, screenshotBlob, category]);

  // Auto-hide the "sent" banner after a short delay.
  useEffect(() => {
    if (!sent) return undefined;
    const tmr = setTimeout(() => setSent(false), 2500);
    return () => clearTimeout(tmr);
  }, [sent]);

  useEffect(() => {
    if (!screenshotBlob) {
      setScreenshotPreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(screenshotBlob);
    setScreenshotPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [screenshotBlob]);

  useEffect(() => {
    if (!open) return;
    installConsoleCapture({ max: 400 });
    const t0 = setTimeout(() => messageRef.current?.focus?.(), 50);
    return () => clearTimeout(t0);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    announceDockOpen('feedback');
    const onOtherDock = (e) => {
      if (e.detail?.id && e.detail.id !== 'feedback') onOpenChange(false);
    };
    window.addEventListener(FLOATING_DOCK_EVENT, onOtherDock);
    return () => window.removeEventListener(FLOATING_DOCK_EVENT, onOtherDock);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (category === 'bug') {
      setIncludeConsole(true);
    } else {
      setIncludeConsole(false);
    }
  }, [category]);

  const context = useMemo(() => ({
    url: typeof window !== 'undefined' ? window.location.href : '',
    currentStep: currentStep || '',
    propertyId: currentProperty?.id || '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    viewport: typeof window !== 'undefined' ? { w: window.innerWidth, h: window.innerHeight } : null,
  }), [currentProperty?.id, currentStep]);

  const canSend = !!currentUser?.uid && !!message.trim() && !submitBusy;

  const handleCapture = async () => {
    setErrMsg('');
    setSent(false);
    setCaptureOk(false);
    setScreenshotBusy(true);
    try {
      const blob = await captureViewportPngBlob();
      if (!blob) throw new Error('capture_failed');
      setScreenshotBlob(blob);
      setCaptureOk(true);
    } catch (e) {
      console.error('Feedback screenshot error:', e);
      setErrMsg(t('feedback.captureError'));
    } finally {
      setScreenshotBusy(false);
    }
  };

  const handleSend = async () => {
    if (!currentUser?.uid) return;
    if (!message.trim()) return;
    setErrMsg('');
    setSent(false);
    setSubmitBusy(true);
    try {
      const consoleTail = includeConsole && category === 'bug'
        ? getConsoleTail({ limit: 200 })
        : [];
      await submitFeedback({
        authorUid: currentUser.uid,
        authorDisplayName: currentUser.displayName || '',
        authorEmail: currentUser.email || '',
        category,
        message,
        context,
        includeConsole: includeConsole && category === 'bug',
        consoleLogTail: consoleTail,
        screenshotBlob,
      });
      setSent(true);
      setMessage('');
      setScreenshotBlob(null);
      setCaptureOk(false);
      setTimeout(() => messageRef.current?.focus?.(), 0);
    } catch (e) {
      console.error('Feedback submit error:', e);
      setErrMsg(t('feedback.sendError'));
    } finally {
      setSubmitBusy(false);
    }
  };

  if (!currentUser) return null;

  const dock = (
    <>
      {open && (
        <div
          className={`fixed ${DOCK_PANEL_BOTTOM} ${DOCK_PANEL_RIGHT} z-[200] ${FLOATING_DOCK_CLASS} pointer-events-auto ${DOCK_PANEL_WIDTH} ${DOCK_PANEL_HEIGHT} bg-white border border-slate-200 rounded-xl shadow-2xl flex flex-col overflow-hidden`}
          role="dialog"
          aria-label={t('feedback.panelTitle')}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50 shrink-0">
            <span className="text-sm font-semibold text-slate-900 truncate pr-2">{t('feedback.panelTitle')}</span>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200 shrink-0"
              aria-label={t('cancel')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
            <div className="space-y-2">
              <div className="text-xs font-semibold text-slate-600">{t('feedback.categoryLabel')}</div>
              <div className="flex flex-col gap-2">
                {CATEGORY_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setCategory(opt.id)}
                    className={[
                      'w-full px-3 py-2 rounded-lg text-xs font-semibold border text-left',
                      category === opt.id
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50',
                    ].join(' ')}
                  >
                    {t(opt.labelKey)}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-semibold text-slate-600" htmlFor="feedback-message">
                {t('feedback.messageLabel')}
              </label>
              <textarea
                id="feedback-message"
                ref={messageRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('feedback.messagePlaceholder')}
                rows={6}
                className="w-full rounded-lg border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>

            {category === 'bug' ? (
              <label className="flex items-center gap-2 text-xs text-slate-700 select-none">
                <input
                  type="checkbox"
                  checked={includeConsole}
                  onChange={(e) => setIncludeConsole(e.target.checked)}
                />
                <span>{t('feedback.includeConsole')}</span>
              </label>
            ) : null}

            {errMsg ? (
              <div className="text-xs text-red-600">{errMsg}</div>
            ) : sent ? (
              <div className="text-xs text-green-700">{t('feedback.sent')}</div>
            ) : captureOk && screenshotBlob ? (
              <div className="text-xs text-green-700">{t('feedback.captureOk')}</div>
            ) : null}

            {screenshotBlob ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 flex items-center gap-2">
                {screenshotPreviewUrl ? (
                  <img
                    src={screenshotPreviewUrl}
                    alt={t('feedback.screenshotAlt')}
                    className="w-16 h-12 object-cover rounded-md border border-slate-200 bg-white"
                  />
                ) : (
                  <div className="w-16 h-12 rounded-md border border-slate-200 bg-white" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-700">{t('feedback.screenshotAttached')}</div>
                  <div className="text-[11px] text-slate-500">
                    {Math.round((screenshotBlob.size || 0) / 1024)} KB
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setScreenshotBlob(null);
                    setCaptureOk(false);
                  }}
                  className="text-xs font-semibold text-slate-600 hover:text-slate-900 px-2 py-1 rounded-md hover:bg-slate-100"
                >
                  {t('feedback.remove')}
                </button>
              </div>
            ) : null}
          </div>

          <div className="border-t border-slate-100 bg-white p-3 flex flex-wrap items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleCapture}
              disabled={screenshotBusy}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              title={t('feedback.capture')}
            >
              <Camera className="w-4 h-4 shrink-0" />
              <span className="text-xs font-semibold">
                {screenshotBusy ? t('feedback.capturing') : t('feedback.capture')}
              </span>
            </button>
            <div className="flex-1 min-w-0" />
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              <Send className="w-4 h-4 shrink-0" />
              <span className="text-xs font-semibold">
                {submitBusy ? t('feedback.sending') : t('feedback.send')}
              </span>
            </button>
          </div>
        </div>
      )}

      <div className={`fixed ${DOCK_BOTTOM} ${DOCK_RIGHT.feedback} z-[200] ${FLOATING_DOCK_CLASS} pointer-events-none`}>
        <button
          type="button"
          onClick={() => onOpenChange(!open)}
          className="pointer-events-auto relative w-14 h-14 rounded-full bg-emerald-600 text-white shadow-lg hover:bg-emerald-700 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-emerald-400"
          aria-expanded={open}
          aria-label={t('feedback.toggle')}
        >
          <MessageSquarePlus className="w-7 h-7" />
        </button>
      </div>
    </>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(dock, document.body);
  }
  return dock;
}

